import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, ExternalLink } from "lucide-react";
import { INQUIRY_STATUS } from "@avestate/shared";
import { useInquiries, usePatchInquiry, type Inquiry } from "@/hooks/useInquiries.js";
import { useRealtimeInquiries } from "@/hooks/useRealtimeInquiries.js";
import { Button } from "@/components/ui/button.js";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer.js";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "@/components/ui/table.js";
import { StatusPill } from "@/components/signature/StatusPill.js";
import { RoleGate } from "@/components/RoleGate.js";

export default function Inquiries(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? undefined;
  const listing_id = params.get("listing_id") ?? undefined;

  const filters = useMemo(() => ({ status, listing_id }), [status, listing_id]);
  const { data } = useInquiries(filters);
  const patch = usePatchInquiry();
  const [open, setOpen] = useState<Inquiry | null>(null);

  useRealtimeInquiries();

  const items = data?.items ?? [];

  async function setStatus(inq: Inquiry, next: typeof INQUIRY_STATUS[number]) {
    try {
      await patch.mutateAsync({ id: inq.id, patch: { status: next } });
      toast.success(`Stato → ${next}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-display text-ink">Richieste</h1>
          <p className="text-body text-ink-soft">{items.length} richieste · realtime attivo</p>
        </div>
        <div className="flex gap-2">
          <select
            value={status ?? ""}
            onChange={(e) => {
              const next = e.target.value;
              const p = new URLSearchParams(params);
              if (next) p.set("status", next); else p.delete("status");
              setParams(p);
            }}
            className="h-9 rounded-sm border border-rule bg-surface-2 px-3 text-body text-ink"
            aria-label="Filtra per stato"
          >
            <option value="">Tutti gli stati</option>
            {INQUIRY_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </header>

      <div className="overflow-x-auto rounded border border-rule bg-surface">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-[110px]">Stato</TableHeader>
              <TableHeader>Mittente</TableHeader>
              <TableHeader className="w-[140px]">Motivo</TableHeader>
              <TableHeader className="w-[180px]">Listing</TableHeader>
              <TableHeader className="w-[170px] text-right">Ricevuta</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.id} onClick={() => setOpen(row)} className="cursor-pointer">
                <TableCell><StatusPill status={row.status} /></TableCell>
                <TableCell>
                  <p className="text-body-strong text-ink">{row.name}</p>
                  <p className="text-meta text-ink-soft">{row.email}</p>
                </TableCell>
                <TableCell className="font-mono text-meta uppercase text-ink-mute">{row.reason}</TableCell>
                <TableCell>
                  {row.listing_id ? (
                    <Link to={`/listings/${row.listing_id}`} className="text-meta text-accent hover:underline">
                      apri
                    </Link>
                  ) : <span className="text-ink-mute">—</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-meta text-ink-mute">
                  {new Date(row.created_at).toLocaleString("it-IT")}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center text-ink-mute">
                  Nessuna richiesta.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <Drawer open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DrawerContent>
          {open ? (
            <div className="flex h-full flex-col">
              <header className="border-b border-rule p-6">
                <DrawerTitle className="text-h2 font-display text-ink">{open.name}</DrawerTitle>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StatusPill status={open.status} />
                  <span className="font-mono text-meta uppercase text-ink-mute">{open.reason}</span>
                  <span className="font-mono text-meta uppercase text-ink-mute">lingua: {open.lang}</span>
                </div>
              </header>

              <div className="flex-1 space-y-5 overflow-y-auto p-6">
                <section className="space-y-2">
                  <h3 className="font-mono text-meta uppercase tracking-wider text-ink-mute">Contatti</h3>
                  <div className="flex items-center gap-2 text-body">
                    <Mail className="size-4 text-ink-mute" />
                    <a href={`mailto:${open.email}`} className="text-accent hover:underline">{open.email}</a>
                  </div>
                  {open.phone ? (
                    <div className="flex items-center gap-2 text-body">
                      <Phone className="size-4 text-ink-mute" />
                      <a href={`tel:${open.phone}`} className="text-accent hover:underline">{open.phone}</a>
                    </div>
                  ) : null}
                </section>

                <section className="space-y-2">
                  <h3 className="font-mono text-meta uppercase tracking-wider text-ink-mute">Messaggio</h3>
                  <p className="whitespace-pre-wrap rounded border border-rule bg-surface-2 p-3 text-body text-ink">
                    {open.message}
                  </p>
                </section>

                {open.listing_id ? (
                  <section>
                    <Link to={`/listings/${open.listing_id}`} className="inline-flex items-center gap-1 text-meta font-mono uppercase tracking-wider text-accent hover:underline">
                      <ExternalLink className="size-3.5" /> apri immobile
                    </Link>
                  </section>
                ) : null}
              </div>

              <RoleGate min="editor">
                <footer className="flex flex-wrap gap-2 border-t border-rule bg-surface-2 p-4">
                  {INQUIRY_STATUS.map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={open.status === s ? "primary" : "outline"}
                      onClick={() => void setStatus(open, s)}
                    >
                      {s}
                    </Button>
                  ))}
                  <Button asChild size="sm" variant="ghost" className="ml-auto">
                    <a href={`mailto:${open.email}?subject=Re: la tua richiesta — av/estate`}>
                      <Mail className="size-3.5" /> Rispondi
                    </a>
                  </Button>
                </footer>
              </RoleGate>
            </div>
          ) : null}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
